import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api, request, setUnauthenticatedHandler, storeToken, storedToken } from '../api/client';
import type { ClientConfig, CurrentUser } from '../api/types';

/**
 * Who is signed in, and what this deployment calls things.
 *
 * Both live here because both are needed by almost every screen and neither changes
 * during a session. Fetching the vocabulary once at sign-in, rather than per screen, is
 * what stops a page rendering "Site" for a moment before correcting itself to "Store".
 */

interface SessionState {
  user: CurrentUser | null;
  /** Never null: the provider falls back to neutral wording before any screen renders. */
  config: ClientConfig;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Whether the signed-in user holds a permission.
   *
   * The panel hides what a user cannot do, but this is presentation only — the server
   * checks every request regardless. A hidden button is a courtesy; the route's
   * `requirePermission` is the control.
   */
  can: (permission: string) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

const DEFAULT_CONFIG: ClientConfig = {
  name: 'xnIQ Audit',
  siteLabel: 'Site',
  subUnitLabel: 'Sub-Unit',
  fieldUserLabel: 'Field User',
  timezone: 'UTC',
  locale: 'en',
};

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (storedToken() === null) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const [me, clientConfig] = await Promise.all([
        api.get<CurrentUser>('/auth/me'),
        // A deployment that has not been configured yet still signs in; it simply falls
        // back to neutral wording rather than failing to render.
        api.get<ClientConfig>('/client').catch(() => DEFAULT_CONFIG),
      ]);
      setUser(me);
      setConfig(clientConfig);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setUser(null);
      setConfig(null);
    });
    void load();
  }, [load]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      // Anonymous for the same reason as the mobile client: a wrong password is not an
      // expired session, and reporting it as one sends the user looking for a problem
      // that does not exist.
      const result = await request<{ user: CurrentUser; accessToken: string }>('/auth/login', {
        method: 'POST',
        body: { username, password },
        anonymous: true,
      });
      storeToken(result.accessToken);
      setUser(result.user);
      setConfig(await api.get<ClientConfig>('/client').catch(() => DEFAULT_CONFIG));
    },
    [],
  );

  const signOut = useCallback(async () => {
    // Told to the server so the session row is revoked. Dropping the token locally alone
    // would leave a working credential behind — SMA's year-long unrevocable tokens (S-12).
    await api.post('/auth/logout').catch(() => undefined);
    storeToken(null);
    setUser(null);
    setConfig(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      user,
      config: config ?? DEFAULT_CONFIG,
      loading,
      signIn,
      signOut,
      refresh: load,
      can: (permission) => user?.permissions.includes(permission) ?? false,
    }),
    [user, config, loading, signIn, signOut, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (context === null) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}

/** Shorthand for the deployment's vocabulary, which nearly every screen needs. */
export function useLabels(): ClientConfig {
  return useSession().config;
}
